import { useCallback, useEffect, useRef } from "react";

/**
 * hook for load more
 * @param onLoadMore - Promise void
 * @param loading - boolean
 */
export default function useLoadMore(onLoadMore: () => Promise<void>, loading?: boolean) {
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef(onLoadMore);
  const loadingRef = useRef(loading);
  const pendingRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    loadingRef.current = loading;
    // IMPORTANT: Do NOT reset pendingRef here when loading becomes false.
  }, [loading]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (!entry.isIntersecting) {
          // Sentinel left the viewport — release the guard so the *next* intentional
          if (!loadingRef.current) {
            pendingRef.current = false;
          }
          return;
        }

        // Prevent duplicate triggers while loading or while a call is pending.
        if (loadingRef.current || pendingRef.current) return;

        pendingRef.current = true;

        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          Promise.resolve()
            .then(async () => {
              // Re-check after debounce — a load may already be in flight.
              if (loadingRef.current) return;
              await callbackRef.current();
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error("Error in onLoadMore:", error);
              pendingRef.current = false;
            });
        }, 200);
      },
      { threshold: 0, rootMargin: "0px 0px 120px 0px" },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  /**
   * Render the sentinel element that triggers load-more when scrolled into view.
   */
  const renderNodeLoader = useCallback(
    () => (
      <div ref={loaderRef} style={{ padding: "10px", textAlign: "center", marginBottom: "0" }} />
    ),
    [],
  );

  return { loaderRef, renderNodeLoader };
}
